import {
  DEFAULT_AVATAR_CONFIG,
  validateAvatarConfig,
  type AvatarConfig,
} from "@guandan/avatar-core";
import type {
  RevealedHands,
  RevealedHandsByRound,
  ViewerSnapshot,
} from "@guandan/protocol";
import type { AuthorityEvent } from "../game/table-controller";

export interface PlayerProfile {
  readonly email: string;
  readonly nickname: string;
  readonly avatar: AvatarConfig;
  readonly level: number;
  readonly experience: number;
  readonly reputation: number;
}

export interface AppSettings {
  readonly music: boolean;
  readonly soundEffects: boolean;
  readonly musicTrack: MusicTrackId;
  readonly customMusicTrackId: string | null;
  readonly customMusicTrackName: string | null;
  readonly musicVolume: number;
  readonly soundEffectsVolume: number;
  readonly cardSize: "small" | "medium" | "large";
  readonly handOrderDirection:
    "ascending-left-to-right" | "ascending-right-to-left";
  readonly textScale: 1 | 1.25 | 1.5 | 2;
  readonly reduced3d: boolean;
  readonly vibration: boolean;
}

export type MusicTrackId = "haikou-night" | "jade-breeze" | "city-glow";

export interface MatchRecord {
  readonly id: string;
  readonly endedAt: number;
  readonly mode: "快速匹配" | "好友房" | "电脑练习";
  readonly roomId: string;
  readonly result: "胜利" | "失败";
  readonly winningTeam?: 0 | 1;
  readonly outcomeBasis?:
    | "through-ace"
    | "final-level"
    | "round-wins"
    | "team-points"
    | "final-round-tiebreak";
  readonly teamResult: "双上" | "胜利" | "双下" | "失利";
  readonly placement: 1 | 2 | 3 | 4;
  readonly advancement: 1 | 2 | 3;
  readonly levelBefore: string;
  readonly levelAfter: string;
  readonly score: number;
  readonly experienceDelta: number;
  readonly reputationDelta: number;
  readonly participants: readonly MatchParticipant[];
  readonly metrics: MatchMetrics;
  readonly events: readonly string[];
  readonly authorityEvents: readonly AuthorityEvent[];
  readonly revealedHandsByRound?: RevealedHandsByRound;
  /** Backward-compatible local cache field from pre-round replays. */
  readonly revealedHands?: RevealedHands;
}

export interface MatchParticipant {
  readonly seat: 0 | 1 | 2 | 3;
  readonly nickname: string;
  readonly team: 0 | 1;
  readonly finishOrder: 1 | 2 | 3 | 4;
  readonly isBot: boolean;
}

export interface MatchMetrics {
  readonly plays: number;
  readonly bombs: number;
  readonly trickRights: number;
  readonly teamAssists: number;
  readonly trusteeSeconds: number;
  readonly disconnects: number;
}

interface StoredState {
  readonly version: 1;
  readonly profile: PlayerProfile | null;
  readonly settings: AppSettings;
  readonly friends: readonly string[];
  readonly matches: readonly MatchRecord[];
}

const KEY = "tongxin.guandan.state.v1";
const DEFAULT_SETTINGS: AppSettings = {
  music: true,
  soundEffects: true,
  musicTrack: "haikou-night",
  customMusicTrackId: null,
  customMusicTrackName: null,
  musicVolume: 0.42,
  soundEffectsVolume: 0.72,
  cardSize: "medium",
  handOrderDirection: "ascending-left-to-right",
  textScale: 1,
  reduced3d: false,
  vibration: true,
};
const DEFAULT_STATE: StoredState = {
  version: 1,
  profile: null,
  settings: DEFAULT_SETTINGS,
  friends: [],
  matches: [],
};
let cache: StoredState | undefined;

function read(): StoredState {
  if (cache) return cache;
  try {
    const value = localStorage.getItem(KEY);
    if (!value) return DEFAULT_STATE;
    const parsed = JSON.parse(value) as Partial<StoredState>;
    cache = {
      version: 1,
      profile: parsed.profile
        ? {
            ...parsed.profile,
            avatar: validateAvatarConfig(parsed.profile.avatar),
          }
        : null,
      settings: normalizeSettings(parsed.settings),
      friends: parsed.friends ?? DEFAULT_STATE.friends,
      matches: (parsed.matches ?? []).filter(isMatchRecord),
    };
    return cache;
  } catch {
    return DEFAULT_STATE;
  }
}

function normalizeSettings(
  settings: Partial<AppSettings> | undefined,
): AppSettings {
  const musicTrack = settings?.musicTrack;
  const validTrack: MusicTrackId =
    musicTrack === "jade-breeze" || musicTrack === "city-glow"
      ? musicTrack
      : "haikou-night";
  const volume = (value: number | undefined, fallback: number) =>
    typeof value === "number" && Number.isFinite(value)
      ? Math.min(1, Math.max(0, value))
      : fallback;
  return {
    ...DEFAULT_SETTINGS,
    ...settings,
    musicTrack: validTrack,
    customMusicTrackId:
      typeof settings?.customMusicTrackId === "string" &&
      settings.customMusicTrackId.length <= 128
        ? settings.customMusicTrackId
        : null,
    customMusicTrackName:
      typeof settings?.customMusicTrackName === "string" &&
      settings.customMusicTrackName.length <= 120
        ? settings.customMusicTrackName
        : null,
    musicVolume: volume(settings?.musicVolume, DEFAULT_SETTINGS.musicVolume),
    soundEffectsVolume: volume(
      settings?.soundEffectsVolume,
      DEFAULT_SETTINGS.soundEffectsVolume,
    ),
    handOrderDirection:
      settings?.handOrderDirection === "ascending-right-to-left"
        ? "ascending-right-to-left"
        : "ascending-left-to-right",
  };
}

function write(next: StoredState) {
  cache = next;
  localStorage.setItem(KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent("tongxin-state"));
}

export const playerStore = {
  snapshot: read,
  saveProfile(email: string, nickname: string, avatar: AvatarConfig) {
    const state = read();
    const profile: PlayerProfile = {
      email,
      nickname,
      avatar,
      level: state.profile?.level ?? 1,
      experience: state.profile?.experience ?? 0,
      reputation: state.profile?.reputation ?? 100,
    };
    write({ ...state, profile });
    return profile;
  },
  restoreProfile(profile: PlayerProfile) {
    const restored: PlayerProfile = {
      ...profile,
      avatar: validateAvatarConfig(profile.avatar),
    };
    write({ ...read(), profile: restored });
    return restored;
  },
  saveAvatar(avatar: AvatarConfig) {
    const state = read();
    if (!state.profile) return;
    write({ ...state, profile: { ...state.profile, avatar } });
  },
  saveSettings(settings: AppSettings) {
    write({ ...read(), settings });
  },
  addFriend(name: string) {
    const state = read();
    if (state.friends.includes(name)) return;
    write({ ...state, friends: [...state.friends, name] });
  },
  addMatch(
    mode: MatchRecord["mode"],
    events: readonly string[],
    snapshot: ViewerSnapshot,
    authorityEvents: readonly AuthorityEvent[],
    revealedHandsByRound?: RevealedHandsByRound,
  ) {
    const state = read();
    const record = buildMatchRecord(
      mode,
      events,
      snapshot,
      authorityEvents,
      state.profile?.nickname,
      revealedHandsByRound,
    );
    const profile =
      state.profile && mode === "电脑练习"
        ? {
            ...state.profile,
            experience: state.profile.experience + record.experienceDelta,
            level: Math.max(
              state.profile.level,
              1 +
                Math.floor(
                  (state.profile.experience + record.experienceDelta) / 100,
                ),
            ),
            reputation: Math.max(
              0,
              Math.min(100, state.profile.reputation + record.reputationDelta),
            ),
          }
        : state.profile;
    write({
      ...state,
      profile,
      matches: [record, ...state.matches].slice(0, 30),
    });
    return record;
  },
  resetForTests() {
    cache = undefined;
    localStorage.removeItem(KEY);
  },
  defaultAvatar: DEFAULT_AVATAR_CONFIG,
};

export function buildMatchRecord(
  mode: MatchRecord["mode"],
  events: readonly string[],
  snapshot: ViewerSnapshot,
  authorityEvents: readonly AuthorityEvent[],
  selfNickname?: string,
  revealedHandsByRound?: RevealedHandsByRound,
): MatchRecord {
  if (snapshot.phase !== "settled" || snapshot.selfSeat === null)
    throw new Error("只有权威结算状态可以写入战绩");
  const participants = snapshot.players
    .map((player): MatchParticipant => {
      if (player.finishOrder === null) throw new Error("权威结算缺少玩家名次");
      return {
        seat: player.seat as 0 | 1 | 2 | 3,
        nickname:
          player.seat === snapshot.selfSeat && selfNickname
            ? selfNickname
            : player.nickname,
        team: player.team as 0 | 1,
        finishOrder: player.finishOrder as 1 | 2 | 3 | 4,
        isBot: player.isBot,
      };
    })
    .sort((left, right) => left.finishOrder - right.finishOrder);
  if (participants.length !== 4) throw new Error("权威结算必须包含四名玩家");
  const self = participants.find((player) => player.seat === snapshot.selfSeat);
  const first = participants[0];
  const second = participants[1];
  const third = participants[2];
  if (!self || !first || !second || !third)
    throw new Error("权威结算玩家数据无效");
  const finalMatchEvent = [...authorityEvents]
    .reverse()
    .find((event) => event.type === "MATCH_SETTLED");
  const nestedSettlement = recordField(finalMatchEvent?.payload.settlement);
  const matchSettlement = nestedSettlement ?? finalMatchEvent?.payload;
  const declaredWinningTeam = numberField(matchSettlement?.winningTeam);
  const winningTeam: 0 | 1 =
    declaredWinningTeam === 0 || declaredWinningTeam === 1
      ? declaredWinningTeam
      : first.team;
  const lastRoundDoubleUp = second.team === first.team;
  const declaredDoubleUp = booleanField(matchSettlement?.doubleUp);
  const doubleUp =
    declaredDoubleUp ?? (winningTeam === first.team && lastRoundDoubleUp);
  const calculatedAdvancement: 1 | 2 | 3 = lastRoundDoubleUp
    ? 3
    : third.team === first.team
      ? 2
      : 1;
  const advancementEvent = [...authorityEvents]
    .reverse()
    .find((event) => event.type === "LEVEL_ADVANCED");
  const advancementValue = numberField(advancementEvent?.payload.advancement);
  const aggregateDelta = numberField(matchSettlement?.levelDelta);
  const advancement =
    aggregateDelta === 1 || aggregateDelta === 2 || aggregateDelta === 3
      ? aggregateDelta
      : advancementValue === 1 ||
          advancementValue === 2 ||
          advancementValue === 3
        ? advancementValue
        : calculatedAdvancement;
  const won = self.team === winningTeam;
  const teamResult = won
    ? doubleUp
      ? "双上"
      : "胜利"
    : doubleUp
      ? "双下"
      : "失利";
  const outcomeBasis = outcomeBasisField(matchSettlement?.outcomeBasis);
  const teamLevels = Array.isArray(matchSettlement?.teamLevels)
    ? matchSettlement.teamLevels
    : null;
  const winnerLevel =
    typeof teamLevels?.[winningTeam] === "string"
      ? teamLevels[winningTeam]
      : null;
  const lastRoundWinningTeam = numberField(
    advancementEvent?.payload.winningTeam,
  );
  const winnerAdvancedLastRound = lastRoundWinningTeam === winningTeam;
  const levelAfter =
    winnerLevel ??
    (winnerAdvancedLastRound
      ? stringField(advancementEvent?.payload.levelAfter)
      : null) ??
    snapshot.levelRank;
  const levelBefore = winnerAdvancedLastRound
    ? (stringField(advancementEvent?.payload.levelBefore) ?? levelAfter)
    : levelAfter;
  const metrics = calculateMetrics(authorityEvents, snapshot.selfSeat);
  const score =
    metrics.plays +
    metrics.bombs * 3 +
    metrics.trickRights * 2 +
    metrics.teamAssists * 2 +
    (won ? advancement * 5 : 0);
  const experienceDelta =
    mode === "电脑练习"
      ? Math.max(5, score + (won ? 12 : 4))
      : won
        ? 12 + advancement
        : 10;
  return {
    id: crypto.randomUUID(),
    endedAt: Date.now(),
    mode,
    roomId: snapshot.roomId,
    result: won ? "胜利" : "失败",
    winningTeam,
    ...(outcomeBasis ? { outcomeBasis } : {}),
    teamResult,
    placement: self.finishOrder,
    advancement,
    levelBefore,
    levelAfter,
    score,
    experienceDelta,
    reputationDelta: 0,
    participants,
    metrics,
    events: [...events],
    authorityEvents: authorityEvents.map((event) => structuredClone(event)),
    ...(revealedHandsByRound
      ? { revealedHandsByRound: structuredClone(revealedHandsByRound) }
      : {}),
  };
}

function calculateMetrics(
  events: readonly AuthorityEvent[],
  selfSeat: number,
): MatchMetrics {
  let plays = 0;
  let bombs = 0;
  let trickRights = 0;
  let teamAssists = 0;
  let disconnects = 0;
  let trusteeMs = 0;
  let takeoverAt: number | null = null;
  let lastPlaySeat: number | null = null;
  for (const event of [...events].sort((left, right) => left.seq - right.seq)) {
    const seat = numberField(event.payload.seat);
    if (event.type === "CARDS_PLAYED") {
      lastPlaySeat = seat;
      if (seat === selfSeat) {
        plays += 1;
        const combination = recordField(event.payload.combination);
        const kind =
          stringField(combination?.kind) ??
          stringField(combination?.normalizationKey) ??
          "";
        if (kind.includes("bomb") || kind.includes("straight-flush"))
          bombs += 1;
      }
    }
    if (
      event.type === "PLAYER_PASSED" &&
      seat === selfSeat &&
      lastPlaySeat !== null &&
      lastPlaySeat % 2 === selfSeat % 2
    )
      teamAssists += 1;
    if (
      event.type === "TRICK_RESET" &&
      numberField(event.payload.leadSeat) === selfSeat
    )
      trickRights += 1;
    if (event.type === "CONTROL_CHANGED" && seat === selfSeat) {
      const control = stringField(event.payload.control);
      const reason = stringField(event.payload.reason);
      if (
        takeoverAt === null &&
        (control === "bot" ||
          control === "bot-takeover" ||
          reason === "disconnect" ||
          reason === "timeout")
      ) {
        takeoverAt = event.createdAt;
        disconnects += reason === "disconnect" ? 1 : 0;
      } else if (
        takeoverAt !== null &&
        (control === "human" ||
          reason === "reconnect" ||
          reason === "safe-node")
      ) {
        trusteeMs += Math.max(0, event.createdAt - takeoverAt);
        takeoverAt = null;
      }
    }
  }
  if (takeoverAt !== null)
    trusteeMs += Math.max(
      0,
      (events.at(-1)?.createdAt ?? takeoverAt) - takeoverAt,
    );
  return {
    plays,
    bombs,
    trickRights,
    teamAssists,
    trusteeSeconds: Math.round(trusteeMs / 1000),
    disconnects,
  };
}

function numberField(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function booleanField(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}
function stringField(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
function recordField(value: unknown): Readonly<Record<string, unknown>> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : null;
}

function outcomeBasisField(value: unknown): MatchRecord["outcomeBasis"] {
  return value === "through-ace" ||
    value === "final-level" ||
    value === "round-wins" ||
    value === "team-points" ||
    value === "final-round-tiebreak"
    ? value
    : undefined;
}

function isMatchRecord(value: unknown): value is MatchRecord {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Partial<MatchRecord>;
  return (
    (record.result === "胜利" || record.result === "失败") &&
    Array.isArray(record.participants) &&
    record.participants.length === 4 &&
    typeof record.roomId === "string"
  );
}
